import { type LogoutOptions } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';
import { colors, Eye, Logout, spacing } from 'component-library';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useProfileV2 } from '#lib/Profile/useProfileV2';
import { type TextType } from '#lib/HeaderMenu/types';
import { useAccessToken } from '#lib/hooks/useAccessToken';
import { getEnvironment } from '#lib/utils/environment';
import { useFlag } from '#lib/hooks/useFlag';
import { userIsUserAdminForAnyPartner } from '../../utils/services';
import { routePathsAdmin } from '../../../lib/routePaths/adminRoutePaths';
import { routePathsHome } from '../../../lib/routePaths/homeRoutePaths';
import UserProfileLink from './UserProfileLink';
import {
  BottomPart,
  DropDownMenuInnerWrapper,
  DropDownMenuWrapper,
  Email,
  HorizontalDivider,
  Link,
  LinkWrapper,
  LogoutButton,
  Name,
  NameDiv,
  UserInfo,
} from './styles';
import ContactSupportLink from './ContactSupportLink';
import { HealthIcon, LocationsIcon, SubscriptionsIcon, TopIcon, UsersIcon } from './UserMenuIcons';

type DropDownMenuProps = {
  logout: (options?: LogoutOptions) => void;
  closeMenu: () => void;
  text: TextType;
};

export const UserMenu = ({ logout, closeMenu, text, ...rest }: DropDownMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [activeHoverItem, setActiveHoverItem] = useState<string | null>(null);

  const { t } = useTranslation();

  const { getAccessTokenSilently } = useAccessToken();

  const { basicProfile } = useProfileV2();

  const mergeMenuFlag = useFlag('merge-cog-and-user-menu');

  const userShouldSeeAdmin = userIsUserAdminForAnyPartner(basicProfile);

  const isSuperUser = !!basicProfile?.aggregatedPermissions.roles.superUser;
  const hasAccessToManageSubscriptions =
    basicProfile?.aggregatedPermissions.roles.manageSubscriptions;
  const userHasSwedishpartner = basicProfile?.aggregatedPermissions.markets.sweden;
  const showSubscriptions =
    (hasAccessToManageSubscriptions && userHasSwedishpartner) || isSuperUser;

  useEffect(() => {
    const handleOutsideClick = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('click', handleOutsideClick, false);

    return () => {
      document.removeEventListener('click', handleOutsideClick, false);
    };
  }, [closeMenu]);

  const navigate = useNavigate();

  const getHoverColor = (item: string) => {
    return activeHoverItem === item ? colors.primary.blue : colors.base.black;
  };

  const isNotProd = getEnvironment() !== 'production';

  return (
    <DropDownMenuWrapper ref={ref} {...rest}>
      <DropDownMenuInnerWrapper>
        <UserInfo>
          <TopIcon />
          <NameDiv>
            <Name>{basicProfile?.fullName}</Name>
            <Email>{basicProfile?.email}</Email>
          </NameDiv>
        </UserInfo>
        <BottomPart>
          <UserProfileLink text={text} closeMenu={closeMenu} />
          <ContactSupportLink text={text} closeMenu={closeMenu} />
          {isNotProd && (
            <LinkWrapper
              onMouseEnter={() => {
                setActiveHoverItem('renewToken');
              }}
              onMouseLeave={() => {
                setActiveHoverItem(null);
              }}
            >
              <Link
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  await getAccessTokenSilently({ cacheMode: 'off' });
                  window.location.reload();
                }}
              >
                <Eye
                  width={spacing.small}
                  height={spacing.small}
                  color={getHoverColor('renewToken')}
                />
                <span>Renew token (for devs)</span>
              </Link>
            </LinkWrapper>
          )}
          {isSuperUser && (
            <LinkWrapper
              onMouseEnter={() => {
                setActiveHoverItem('systemHealth');
              }}
              onMouseLeave={() => {
                setActiveHoverItem(null);
              }}
            >
              <Link
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  closeMenu();
                  await navigate('/health');
                }}
              >
                <HealthIcon color={getHoverColor('systemHealth')} />
                <span>System health</span>
              </Link>
            </LinkWrapper>
          )}
          {isSuperUser && (
            <LinkWrapper
              onMouseEnter={() => {
                setActiveHoverItem('subscriptionsOverview');
              }}
              onMouseLeave={() => {
                setActiveHoverItem(null);
              }}
            >
              <Link
                href={routePathsHome.SUBSCRIPTIONS_OVERVIEW}
                onClick={async (e) => {
                  e.preventDefault();
                  closeMenu();
                  await navigate(routePathsHome.SUBSCRIPTIONS_OVERVIEW);
                }}
              >
                <SubscriptionsIcon color={getHoverColor('subscriptionsOverview')} />
                <span>{t('subscription.admin.overview.menu')}</span>
              </Link>
            </LinkWrapper>
          )}

          {mergeMenuFlag && userShouldSeeAdmin && (
            <>
              <HorizontalDivider />
              <LinkWrapper
                onMouseEnter={() => {
                  setActiveHoverItem('users');
                }}
                onMouseLeave={() => {
                  setActiveHoverItem(null);
                }}
              >
                <Link
                  href={routePathsAdmin.USERS}
                  onClick={async (e) => {
                    e.preventDefault();
                    closeMenu();
                    await navigate(routePathsAdmin.USERS);
                  }}
                >
                  <UsersIcon
                    color={activeHoverItem === 'users' ? colors.primary.blue : colors.base.black}
                  />
                  <span>{t('admin.menu.users')}</span>
                </Link>
              </LinkWrapper>
              <LinkWrapper
                onMouseEnter={() => {
                  setActiveHoverItem('locations');
                }}
                onMouseLeave={() => {
                  setActiveHoverItem(null);
                }}
              >
                <Link
                  href={routePathsAdmin.LOCATIONS}
                  onClick={async (e) => {
                    e.preventDefault();
                    closeMenu();
                    await navigate(routePathsAdmin.LOCATIONS);
                  }}
                >
                  <LocationsIcon color={getHoverColor('locations')} />
                  <span>{t('admin.menu.locations')}</span>
                </Link>
              </LinkWrapper>

              {showSubscriptions && (
                <LinkWrapper
                  onMouseEnter={() => {
                    setActiveHoverItem('subscriptions');
                  }}
                  onMouseLeave={() => {
                    setActiveHoverItem(null);
                  }}
                >
                  <Link
                    href={routePathsAdmin.SUBSCRIPTIONS}
                    onClick={async (e) => {
                      e.preventDefault();
                      closeMenu();
                      await navigate(routePathsAdmin.SUBSCRIPTIONS);
                    }}
                  >
                    <SubscriptionsIcon color={getHoverColor('subscriptions')} />
                    <span>{t('admin.menu.subscriptions')}</span>
                  </Link>
                </LinkWrapper>
              )}
            </>
          )}

          <HorizontalDivider />

          <LogoutButton
            data-testid="logout-button"
            onClick={() => {
              logout();
            }}
            onMouseEnter={() => {
              setActiveHoverItem('logout');
            }}
            onMouseLeave={() => {
              setActiveHoverItem(null);
            }}
          >
            <Logout width={spacing.small} height={spacing.small} color={getHoverColor('logout')} />

            <span>{text.signOut}</span>
          </LogoutButton>
        </BottomPart>
      </DropDownMenuInnerWrapper>
    </DropDownMenuWrapper>
  );
};
